<script lang="ts">
	import Letterhead from '$lib/components/Letterhead.svelte';
	import { canonicalUrl, jsonLdGraph, ogImageUrl, pageTitle } from '$lib/seo';

	let { data } = $props();
	const meta = $derived(data.seo!);

	const lastEdited = 'May 3, 2026';
</script>

<svelte:head>
	<title>{pageTitle(meta)}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={canonicalUrl(meta)} />
	{#if meta.robots}
		<meta name="robots" content={meta.robots} />
	{/if}
	<meta property="og:type" content="website" />
	<meta property="og:url" content={canonicalUrl(meta)} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:image" content={ogImageUrl(meta)} />
	<meta name="twitter:card" content="summary_large_image" />
	{@html `<script type="application/ld+json">${jsonLdGraph(meta)}</script>`}
</svelte:head>

<article class="privacy">
	<div class="container-wide">
		<Letterhead>
			<header class="privacy-head">
				<h1 class="privacy-title">On privacy.</h1>
				<p class="privacy-lede">
					What we collect on this website, what we do not, and what you can ask for at any time.
				</p>
			</header>

			<section class="block">
				<h2>What this website collects.</h2>
				<p>
					When you visit, our hosting provider records the request — the URL, the time, your IP
					address, and the user agent — for the period required to keep the site online and detect
					abuse. We do not sell these logs.
				</p>
				<p>
					If you submit the design-partner application, the form fields you typed are sent to our
					transactional email provider, Resend, which delivers them to our inbox. The form stores
					nothing else. No cookies are set by the form.
				</p>
				<p>
					If you accept analytics (the small note from us that appears for visitors in the EU, EEA,
					UK, and Switzerland), Google Analytics 4 stores a measurement identifier in your browser.
					We use it to count readers, not to profile you. We have disabled all advertising features
					on the property. You can decline; we will respect that choice and send no analytics
					events.
				</p>
			</section>

			<section class="block">
				<h2>What Ekavi (the product) collects.</h2>
				<p>
					This privacy notice covers the website. Inside the Ekavi product, your patient data (PHI)
					is governed by a Business Associate Agreement we sign with your practice. The full
					architecture, retention rules, and incident-response posture are on the
					<a href="/trust/">trust page</a>. PHI does not train our models. Retention is ninety days
					unless you choose otherwise.
				</p>
			</section>

			<section class="block">
				<h2>Your rights, region by region.</h2>
				<p><strong>EU / EEA / UK / Switzerland (GDPR Art. 13/14, UK-GDPR, FADP).</strong></p>
				<p>
					You can ask us what we hold about you, ask us to correct it, ask us to delete it, ask us
					to stop using it, or ask for a copy. Write to
					<a href="mailto:privacy@ekavi.care">privacy@ekavi.care</a>; we reply within a working
					week. Our lawful basis is legitimate interest (operating this website) for the access logs
					and consent for analytics. The data controller is Ekavi Care; the legal entity is being
					established in the United States and will be named here at launch.
				</p>
				<p><strong>California (CCPA / CPRA).</strong></p>
				<p>
					We do not sell or share your personal information for cross-context behavioral
					advertising. You have the right to know, the right to delete, the right to correct, and
					the right to limit. To exercise any of these, write to
					<a href="mailto:privacy@ekavi.care">privacy@ekavi.care</a>.
				</p>
			</section>

			<section class="block">
				<h2>HIPAA Notice of Privacy Practices.</h2>
				<p>
					The federal NPP update for reproductive health information takes effect February 16, 2026.
					If your practice uses Ekavi, the templates we ship for your NPP have been updated
					accordingly. The product NPP is delivered with onboarding, alongside the BAA.
				</p>
			</section>

			<section class="block">
				<h2>How to write to us.</h2>
				<ul>
					<li>Privacy questions: <a href="mailto:privacy@ekavi.care">privacy@ekavi.care</a></li>
					<li>Trust / compliance: <a href="mailto:trust@ekavi.care">trust@ekavi.care</a></li>
					<li>Anything else: <a href="mailto:hello@ekavi.care">hello@ekavi.care</a></li>
				</ul>
			</section>

			<footer class="privacy-foot">
				<hr class="privacy-rule" />
				<p class="privacy-meta">Last edited {lastEdited}.</p>
			</footer>
		</Letterhead>
	</div>
</article>

<style>
	.privacy {
		padding: var(--ekavi-space-12) 0 var(--ekavi-space-24);
	}
	.privacy-head {
		margin-bottom: var(--ekavi-space-12);
	}
	.privacy-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-40);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
	}
	@media (max-width: 640px) {
		.privacy-title {
			font-size: var(--ekavi-text-28);
		}
	}
	.privacy-lede {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-20);
		color: var(--ekavi-ink-2);
		line-height: var(--ekavi-leading-body);
		max-width: 38ch;
		margin: 0;
	}

	.block {
		padding: var(--ekavi-space-9) 0;
		border-top: var(--ekavi-stroke-hairline);
	}
	.block:first-of-type {
		border-top: none;
	}
	.block h2 {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-20);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-4);
	}
	.block p {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: var(--ekavi-leading-body);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-4);
		max-width: 56ch;
	}
	.block ul {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		color: var(--ekavi-ink-1);
		padding-left: var(--ekavi-space-4);
		margin: 0;
	}
	.block li {
		padding: var(--ekavi-space-2) 0;
		list-style: none;
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.block li:last-child {
		border-bottom: none;
	}

	.privacy-foot {
		margin-top: var(--ekavi-space-12);
	}
	.privacy-rule {
		border: none;
		border-top: var(--ekavi-stroke-hairline);
		margin: 0 0 var(--ekavi-space-9);
	}
	.privacy-meta {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		margin: 0;
	}
</style>
